import type { TNotes } from "../types/index.type";
import { EventType, trackEvent } from "./analytics";
import { getChunkStats } from "./audioStore";

export type LocalBackupStats = {
  localBytes: number;
  localChunkCount: number;
};

/**
 * Returns the bytes/chunks currently backed up locally (Cache/IndexedDB) for a recording.
 * Used to classify a failure as recoverable (bytes exist, user can retry) vs. unrecoverable.
 */
export const getLocalBackupStats = async (recordingId: number | string): Promise<LocalBackupStats> => {
  const stats = await getChunkStats(recordingId.toString()).catch(() => null);
  return { localBytes: stats?.totalBytes ?? 0, localChunkCount: stats?.count ?? 0 };
};

/**
 * Catastrophic case: a retry/process attempt found no audio bytes in Cache/IndexedDB,
 * so the recording can no longer be recovered from this device.
 *
 * @param source where the unrecoverable state was detected (e.g. "retry_upload", "process_now").
 */
export const reportRecordingUnrecoverable = async (note: TNotes, source: string): Promise<void> => {
  const { localBytes, localChunkCount } = await getLocalBackupStats(note.id);
  trackEvent(EventType.RECORDING_UNRECOVERABLE, {
    recordingId: note.id,
    modalityType: note.modality_type,
    noteType: note.note_type,
    errorStatus: note.error_status,
    source,
    localBytes,
    localChunkCount,
  });
};
